/**
 * @fileoverview Centralized Query Key Factory
 * 
 * Provides type-safe, consistent cache keys for React Query.
 * All query keys are centralized here to prevent cache invalidation bugs
 * and enable predictable data fetching patterns.
 * 
 * @module lib/query-keys
 * @version 1.0.0
 * @since 2024-01-01
 * 
 * @architecture
 * ```
 * Query Keys Factory
 * ├── auth.*          - Authentication & session
 * ├── companies.*     - Company management
 * ├── jobs.*          - Job listings
 * ├── applications.*  - Job applications
 * ├── profile.*       - User profiles
 * ├── network.*       - Social networking
 * ├── resume.*        - Resume management
 * ├── notifications.* - User notifications
 * └── sms.*           - SMS messaging
 * ```
 * 
 * @example
 * // Using query keys in a hook
 * import { queryKeys } from '@/lib/query-keys';
 * 
 * const { data } = useQuery({
 *   queryKey: queryKeys.profile.detail(userId),
 *   queryFn: fetchProfile,
 * });
 * 
 * // Invalidating related queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.profile.detail(userId) });
 * 
 * @benefits
 * - Type-safe: TypeScript ensures correct key structure
 * - Centralized: Single source of truth for all cache keys
 * - Hierarchical: Easy partial invalidation (e.g., all jobs)
 * - Consistent: Prevents typos and key mismatches
 * 
 * @see {@link https://tanstack.com/query/latest/docs/react/guides/query-keys} - TanStack Query Keys Guide
 */

/**
 * Centralized query key factory for type-safe and consistent cache management.
 * 
 * Each domain has its own namespace with factory functions that return
 * readonly tuples for maximum type safety and cache predictability.
 * 
 * @constant
 * @type {Object}
 * 
 * @property {Object} auth - Authentication and session keys
 * @property {Function} auth.user - Current user query key
 * @property {Function} auth.session - Active session query key
 * @property {Function} auth.onboardingStatus - User onboarding status key
 * 
 * @property {Object} companies - Company management keys
 * @property {Function} companies.all - All companies for a user
 * @property {Function} companies.detail - Single company by ID
 * @property {Function} companies.members - Company members list
 * 
 * @property {Object} jobs - Job listing keys
 * @property {Function} jobs.all - All jobs
 * @property {Function} jobs.byCompany - Jobs filtered by company
 * @property {Function} jobs.detail - Single job by ID
 * @property {Function} jobs.active - Active jobs only
 * 
 * @property {Object} applications - Job application keys
 * @property {Function} applications.all - All applications for a user
 * @property {Function} applications.byJob - Applications for a specific job
 * @property {Function} applications.detail - Single application by ID
 * @property {Function} applications.stats - Application statistics
 * 
 * @property {Object} profile - User profile keys
 * @property {Function} profile.detail - User profile by ID
 * @property {Function} profile.workHistory - User work history
 * @property {Function} profile.credentials - User credentials/certifications
 * @property {Function} profile.public - Public profile view
 * 
 * @property {Object} network - Social networking keys
 * @property {Function} network.people - People search with filters
 * @property {Function} network.jobs - Network job search with filters
 * 
 * @property {Object} resume - Resume management keys
 * @property {Function} resume.all - All resumes for a user
 * @property {Function} resume.detail - Single resume by ID
 * @property {Function} resume.versions - Resume version history
 * 
 * @property {Object} notifications - Notification keys
 * @property {Function} notifications.all - All notifications for a user
 * @property {Function} notifications.unread - Unread notifications count
 * 
 * @property {Object} sms - SMS messaging keys
 * @property {Function} sms.messages - SMS messages with filters
 * @property {Function} sms.message - Single SMS message by ID
 * @property {Function} sms.analytics - SMS analytics by time range
 * @property {Function} sms.webhooks - Webhook configurations
 */
export const queryKeys = {
  // ────────────────────────────────────────────────────────────────
  // Auth - Authentication & Session Management
  // ────────────────────────────────────────────────────────────────
  auth: {
    /** @returns Query key for current authenticated user */
    user: () => ['auth', 'user'] as const,
    
    /** @returns Query key for active auth session */
    session: () => ['auth', 'session'] as const,
    
    /**
     * @param userId - Optional user ID for specific user's onboarding
     * @returns Query key for onboarding status
     */
    onboardingStatus: (userId?: string) => ['onboarding-status', userId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Companies - Company Management
  // ────────────────────────────────────────────────────────────────
  companies: {
    /**
     * @param userId - User ID to fetch companies for
     * @returns Query key for all user's companies
     */
    all: (userId: string) => ['companies', userId] as const,
    
    /**
     * @param companyId - Company ID
     * @returns Query key for single company detail
     */
    detail: (companyId: string) => ['companies', 'detail', companyId] as const,
    
    /**
     * @param companyId - Company ID
     * @returns Query key for company members list
     */
    members: (companyId: string) => ['companies', 'members', companyId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Jobs - Job Listings
  // ────────────────────────────────────────────────────────────────
  jobs: {
    /** @returns Query key for all jobs */
    all: () => ['jobs'] as const,
    
    /**
     * @param companyId - Company ID to filter by
     * @returns Query key for company's jobs
     */
    byCompany: (companyId: string) => ['jobs', 'company', companyId] as const,
    
    /**
     * @param jobId - Job ID
     * @returns Query key for single job detail
     */
    detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
    
    /** @returns Query key for active jobs only */
    active: () => ['jobs', 'active'] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Applications - Job Applications
  // ────────────────────────────────────────────────────────────────
  applications: {
    /**
     * @param userId - User ID
     * @returns Query key for all user's applications
     */
    all: (userId: string) => ['applications', userId] as const,
    
    /**
     * @param jobId - Job ID
     * @returns Query key for applications to a specific job
     */
    byJob: (jobId: string) => ['applications', 'job', jobId] as const,
    
    /**
     * @param applicationId - Application ID
     * @returns Query key for single application detail
     */
    detail: (applicationId: string) => ['applications', 'detail', applicationId] as const,
    
    /**
     * @param userId - User ID
     * @returns Query key for application statistics
     */
    stats: (userId: string) => ['applications', 'stats', userId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Profile - User Profiles
  // ────────────────────────────────────────────────────────────────
  profile: {
    /**
     * @param userId - User ID
     * @returns Query key for user profile detail
     */
    detail: (userId: string) => ['profile', userId] as const,
    
    /**
     * @param userId - User ID
     * @returns Query key for user's work history
     */
    workHistory: (userId: string) => ['profile', 'work-history', userId] as const,
    
    /**
     * @param userId - User ID
     * @returns Query key for user's credentials/certifications
     */
    credentials: (userId: string) => ['profile', 'credentials', userId] as const,
    
    /**
     * @param userId - User ID
     * @returns Query key for public profile view
     */
    public: (userId: string) => ['profile', 'public', userId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Network - Social Networking
  // ────────────────────────────────────────────────────────────────
  network: {
    /**
     * @param filters - Search/filter criteria
     * @returns Query key for people search
     */
    people: (filters: any) => ['network', 'people', filters] as const,
    
    /**
     * @param filters - Search/filter criteria
     * @returns Query key for network job search
     */
    jobs: (filters: any) => ['network', 'jobs', filters] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Resume - Resume Management
  // ────────────────────────────────────────────────────────────────
  resume: {
    /**
     * @param userId - User ID
     * @returns Query key for all user's resumes
     */
    all: (userId: string) => ['resumes', userId] as const,
    
    /**
     * @param resumeId - Resume ID
     * @returns Query key for single resume detail
     */
    detail: (resumeId: string) => ['resumes', 'detail', resumeId] as const,
    
    /**
     * @param resumeId - Resume ID
     * @returns Query key for resume version history
     */
    versions: (resumeId: string) => ['resumes', 'versions', resumeId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // Notifications - User Notifications
  // ────────────────────────────────────────────────────────────────
  notifications: {
    /**
     * @param userId - User ID
     * @returns Query key for all user's notifications
     */
    all: (userId: string) => ['notifications', userId] as const,
    
    /**
     * @param userId - User ID
     * @returns Query key for unread notification count
     */
    unread: (userId: string) => ['notifications', 'unread', userId] as const,
  },
  
  // ────────────────────────────────────────────────────────────────
  // SMS - SMS Messaging
  // ────────────────────────────────────────────────────────────────
  sms: {
    /**
     * @param filters - Message filter criteria
     * @returns Query key for SMS messages
     */
    messages: (filters: any) => ['sms', 'messages', filters] as const,
    
    /**
     * @param id - Message ID
     * @returns Query key for single SMS message
     */
    message: (id: string) => ['sms', 'message', id] as const,
    
    /**
     * @param timeRange - Time range for analytics (e.g., '7d', '30d')
     * @returns Query key for SMS analytics
     */
    analytics: (timeRange: string) => ['sms', 'analytics', timeRange] as const,
    
    /**
     * @param filters - Webhook filter criteria
     * @returns Query key for webhook configurations
     */
    webhooks: (filters: any) => ['sms', 'webhooks', filters] as const,
  },
};
